import React, { useState, useEffect } from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { ShieldExclamationIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { contentManagementService, BlockedContent } from '../../services/contentManagementService';
import toast from 'react-hot-toast';

const AdminBlockedContentPage: React.FC = () => {
  const [blockedContent, setBlockedContent] = useState<BlockedContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockFormData, setBlockFormData] = useState({
    contentId: '',
    contentType: 'POST' as 'POST' | 'COMMENT' | 'USER',
    reason: '',
  });

  useEffect(() => {
    loadBlockedContent();
  }, []);

  const loadBlockedContent = async () => {
    try {
      setLoading(true);
      const data = await contentManagementService.getBlockedContent();
      setBlockedContent(data);
    } catch (error) {
      console.error('Failed to load blocked content:', error);
      toast.error('Failed to load blocked content');
    } finally {
      setLoading(false);
    }
  };

  const handleBlock = async () => {
    try {
      const blockedItem = await contentManagementService.blockContent(blockFormData);
      setBlockedContent([...blockedContent, blockedItem]);
      setShowBlockModal(false);
      setBlockFormData({ contentId: '', contentType: 'POST', reason: '' });
      toast.success('Content blocked successfully');
    } catch (error) {
      console.error('Failed to block content:', error);
      toast.error('Failed to block content');
    }
  };

  const handleUnblock = async (id: string) => {
    if (!confirm('Are you sure you want to unblock this content?')) return;
    try {
      await contentManagementService.unblockContent(id);
      setBlockedContent(blockedContent.filter(item => item.id !== id));
      toast.success('Content unblocked successfully');
    } catch (error) {
      console.error('Failed to unblock content:', error);
      toast.error('Failed to unblock content');
    }
  };

  const closeModal = () => {
    setShowBlockModal(false);
    setBlockFormData({ contentId: '', contentType: 'POST', reason: '' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Blocked Content Management</h1>
        <Button onClick={() => setShowBlockModal(true)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Block Content
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Blocked Items</p>
                <p className="text-2xl font-bold text-gray-900">{blockedContent.length}</p>
              </div>
              <ShieldExclamationIcon className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Blocks</p>
                <p className="text-2xl font-bold text-gray-900">
                  {blockedContent.filter(item => item.isActive).length}
                </p>
              </div>
              <ShieldExclamationIcon className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Content ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blocked By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {blockedContent.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                      {item.contentId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        item.contentType === 'POST' ? 'bg-blue-100 text-blue-800' :
                        item.contentType === 'COMMENT' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {item.contentType}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {item.reason}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.blockedBy}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(item.blockedAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        item.isActive ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {item.isActive ? 'Blocked' : 'Unblocked'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.isActive && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleUnblock(item.id)}
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {blockedContent.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      No blocked content found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Block Content Modal */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Block Content</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content ID</label>
                <input
                  type="text"
                  value={blockFormData.contentId}
                  onChange={(e) => setBlockFormData({ ...blockFormData, contentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="post_123, comment_456, user_789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content Type</label>
                <select
                  value={blockFormData.contentType}
                  onChange={(e) => setBlockFormData({ ...blockFormData, contentType: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="POST">Post</option>
                  <option value="COMMENT">Comment</option>
                  <option value="USER">User</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={blockFormData.reason}
                  onChange={(e) => setBlockFormData({ ...blockFormData, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows={3}
                  placeholder="Reason for blocking this content"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="secondary" onClick={closeModal}>
                Cancel
              </Button>
              <Button onClick={handleBlock}>
                Block Content
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlockedContentPage;